import {callAPI} from '@/store/utils';

const FYGR_COMPANY = 'FYGR_COMPANY';

// Spec §4.1: the paywall status is polled on authenticated navigation, so we
// keep a short client-side cache to avoid hitting the endpoint on every route.
const STATUS_TTL_MS = 60 * 1000;

// Voiture balai: every Nth status call, bypass the client cache to force the
// backend to recompute the outstanding balance live from Stripe. Guards against
// a stale cache (or a missed unblock webhook) keeping an outdated amount/state.
const STATUS_SWEEP_EVERY = 5;

export default {
  namespaced: true,
  state: {
    // Last `/v2/paywall/status` result (see PaywallAPI.py). Null until fetched.
    status: null,
    statusFetchedAt: 0,
    // Rolling count of status calls, drives the "voiture balai" forced refresh.
    statusCallCount: 0,
    // Last `/v2/paywall/catch-up` result: {checkout_url, invoice_id, reused}.
    catchUp: null,
  },
  mutations: {
    setStatus(state, status) {
      state.status = status;
      state.statusFetchedAt = Date.now();
    },
    incrementStatusCallCount(state) {
      state.statusCallCount += 1;
    },
    setCatchUp(state, catchUp) {
      state.catchUp = catchUp;
    },
    resetPaywall(state) {
      state.status = null;
      state.statusFetchedAt = 0;
      state.statusCallCount = 0;
      state.catchUp = null;
    },
  },
  getters: {
    accountBlocked: (state) => !!state.status?.account_blocked,
    paymentStatus: (state) => state.status?.payment_status ?? null,
    paymentFailureReason: (state) => state.status?.payment_failure_reason ?? null,
    failedSince: (state) => state.status?.failed_since ?? null,
    outstandingAmountCents: (state) => state.status?.outstanding_amount_cents ?? 0,
    lines: (state) => state.status?.lines ?? [],
  },
  actions: {
    /**
     * Fetch `/v2/paywall/status`. Cached for STATUS_TTL_MS unless `force` is set
     * (e.g. while polling for the post-payment unblock).
     */
    async actionGetPaywallStatus({commit, state}, {force = false} = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      if (!companyId) return null;
      commit('incrementStatusCallCount');
      // Voiture balai: every Nth call, force a fresh recompute regardless of cache.
      const sweep = state.statusCallCount % STATUS_SWEEP_EVERY === 0;
      if (
        !force &&
        !sweep &&
        state.status &&
        Date.now() - state.statusFetchedAt < STATUS_TTL_MS
      ) {
        return state.status;
      }
      const res = await callAPI('POST', 'paywall/status', {companyId});
      const result = res.result || null;
      commit('setStatus', result);
      return result;
    },
    /**
     * Generate (or reuse) the consolidated catch-up invoice and return a hosted
     * Stripe Checkout Session URL to redirect the user to. Idempotent
     * server-side (an in-flight invoice + open session are reused).
     */
    async actionCatchUp({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'paywall/catch-up', {companyId});
      const result = res.result || null;
      commit('setCatchUp', result);
      return result;
    },
  },
};
